// =============================================================================
// ConfirmDialog / PromptDialog — styled replacements for confirm() and prompt()
// =============================================================================
// The native dialogs block the main thread, ignore the app's theme entirely,
// and on mobile render as a jarring browser chrome sheet. Worse, they're
// unstyleable, so a "Delete this server permanently" prompt looked exactly as
// urgent as "Rename category" — the destructive weight never came through.
//
// These build on the existing `Modal` primitive (ui.tsx), so they inherit its
// Escape handling, click-outside dismiss, focus trap, and dialog semantics for
// free. Don't hand-roll another overlay.
//
// Both are ordinary controlled components, NOT promise-returning helpers, so a
// call site can't do `if (await confirm(...))` inline — it holds the pending
// subject in state instead:
//
//   const [pendingDelete, setPendingDelete] = useState<Channel | null>(null);
//   ...
//   onClick={() => setPendingDelete(channel)}
//   ...
//   <ConfirmDialog
//     open={!!pendingDelete}
//     title="Delete channel"
//     body={<>Delete <b>#{pendingDelete?.name}</b>? This cannot be undone.</>}
//     confirmLabel="Delete"
//     destructive
//     onConfirm={() => { doDelete(pendingDelete!); setPendingDelete(null); }}
//     onCancel={() => setPendingDelete(null)}
//   />
//
// For `.map`-rendered rows, hoist that state to the LIST rather than rendering
// a dialog per row — the same rule the codebase already follows for
// useContextMenu().
// =============================================================================

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Modal, Button, Input } from './ui';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  /** Confirm button text. Keep it a verb ("Delete", "Kick"), not "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm action in the danger treatment. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Focus the confirm button on open so Enter works immediately and screen
  // readers land on the action rather than the dialog chrome. For destructive
  // actions we focus CANCEL instead — a reflexive Enter should not delete
  // something. (Escape cancels in both cases, via Modal.)
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const target = destructive ? cancelRef.current : confirmRef.current;
    // Defer past Modal's own focus-into-dialog effect.
    const t = setTimeout(() => target?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, destructive]);

  return (
    <Modal open={open} onClose={onCancel} title={title}>
      <div className="text-sm text-text leading-relaxed">{body}</div>
      <div className="mt-4 flex justify-end gap-2">
        <Button ref={cancelRef} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          ref={confirmRef}
          variant={destructive ? 'danger' : 'primary'}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

interface PromptDialogProps {
  open: boolean;
  title: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  maxLength?: number;
  /** Called with the trimmed value. Never fires with an empty string. */
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  open,
  title,
  label,
  initialValue = '',
  placeholder,
  confirmLabel = 'Save',
  maxLength = 100,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset to initialValue each time it opens — the component stays mounted
  // between uses, so without this a rename would show the previous target's
  // name.
  useEffect(() => {
    if (!open) return;
    setValue(initialValue);
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => clearTimeout(t);
  }, [open, initialValue]);

  const trimmed = value.trim();
  const submit = () => {
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <Modal open={open} onClose={onCancel} title={title}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          ref={inputRef}
          label={label}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!trimmed}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
